import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  AdDetails,
  GetAdsResponse,
  GetPopularRequestsResponse,
} from '@/api/types/ads.types';
import {
  CategoryBaseInfo,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { GetRegionsResponse } from '@/api/types/regions.types';
import { Ad } from '@/api/types/common.types';

interface ServerDataContextProps {
  initialAdDetails: AdDetails | null;
  initialPremiumAds: GetAdsResponse | null;
  initialVipAds: Ad[] | null;
  initialLatestAds: Ad[] | null;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
  currentCategory: CategoryBaseInfo | null;
  regions: GetRegionsResponse | null;
  h1: string | null;
}

const ServerDataContext = createContext<ServerDataContextProps | undefined>(
  undefined
);

export const ServerDataProvider = ({
  children,
  adDetails,
  premiumAds,
  vipAds,
  latestAds,
  popularRequests,
  rootCategories,
  currentCategory,
  regions,
  h1,
}: {
  children: ReactNode;
  adDetails?: AdDetails | null;
  premiumAds?: GetAdsResponse | null;
  vipAds?: Ad[] | null;
  latestAds?: Ad[] | null;
  popularRequests?: GetPopularRequestsResponse | null;
  rootCategories?: GetRootCategoriesResponse | null;
  currentCategory?: CategoryBaseInfo | null;
  regions?: GetRegionsResponse | null;
  h1?: string | null;
}) => {
  const [resettableData, setResettableData] = useState({
    regions: regions ?? null,
    initialPremiumAds: premiumAds ?? null,
    popularRequests: popularRequests ?? null,
    rootCategories: rootCategories ?? null,
    currentCategory: currentCategory ?? null,
  });

  // Reset server-only data on the client side.
  // Keep initialPremiumAds, popularRequests, rootCategories
  // so ISR pages can use them as fallback while client queries load.
  useEffect(() => {
    setResettableData(prev => ({
      ...prev,
      regions: null,
      currentCategory: null,
    }));
  }, []);

  return (
    <ServerDataContext.Provider
      value={{
        initialAdDetails: adDetails ?? null,
        initialVipAds: vipAds ?? null,
        initialLatestAds: latestAds ?? null,
        h1: h1 ?? null,
        ...resettableData,
      }}
    >
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerDataContext = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error(
      'useServerDataContext must be used within ServerDataProvider'
    );
  }
  return context;
};
